// Core domain types shared by server, web client and balance simulations.

export const ATTRS = ['str', 'agi', 'int', 'vit', 'per'] as const;
export type Attr = (typeof ATTRS)[number];
export type Attributes = Record<Attr, number>;

export const RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const;
export type Rarity = (typeof RARITIES)[number];

/** Equipment slots, head to toe, then carry. */
export const GEAR_SLOTS = ['weapon', 'helmet', 'body', 'gloves', 'pants', 'boots', 'backpack', 'accessory'] as const;
export type GearSlot = (typeof GEAR_SLOTS)[number];

/** The five armor slots that make up a set. Weapon, backpack and accessory never count. */
export const ARMOR_SLOTS = ['helmet', 'body', 'gloves', 'pants', 'boots'] as const;
export type ArmorSlot = (typeof ARMOR_SLOTS)[number];

// 11. 10. 2026 (BACKLOG #36): the Foundry set (heavy armor of T3); its outfit pictures came the same day (`OUTFIT_SET_IDS`).
export const SET_IDS = ['drifter', 'worker', 'scrapper', 'rider', 'hunter', 'medic', 'nightstalker', 'military', 'foundry'] as const;
export type SetId = (typeof SET_IDS)[number];
/** 11. 10. 2026: the sets that have outfits (pictures for both looks); the Foundry's came the same day (Asset Bible, "Crew i Foundry outfit"). */
export const OUTFIT_SET_IDS = ['drifter', 'worker', 'scrapper', 'rider', 'hunter', 'medic', 'nightstalker', 'military', 'foundry'] as const satisfies readonly SetId[];
export type OutfitSetId = (typeof OUTFIT_SET_IDS)[number];

/** Set effects that are not plain stats; each one is a named hook in combat or the expedition tick. */
export type SetHookId = 'drifter_first_escape' | 'scrapper_reflect' | 'rider_no_follow' | 'hunter_intel' | 'medic_retreat_heal' | 'nightstalker_tracking' | 'military_zone';

export const BACKGROUNDS = ['soldier', 'medic', 'engineer', 'hunter', 'scavenger'] as const;
export type BackgroundId = (typeof BACKGROUNDS)[number];

export const STANCES = ['peaceful', 'cautious', 'aggressive', 'shadow'] as const;
export type Stance = (typeof STANCES)[number];

export const RETREATS = ['50', '25', 'never'] as const;
export type Retreat = (typeof RETREATS)[number];

export const AMBUSH_STYLES = ['steal', 'attack'] as const;
export type AmbushStyle = (typeof AMBUSH_STYLES)[number];

export type EnemyType = 'infected' | 'raider' | 'mutant' | 'drone' | 'survivor' | 'boss';

export const WEAPON_CLASSES = ['melee_1h', 'melee_2h', 'ranged_1h', 'ranged_2h'] as const;
export type WeaponClass = (typeof WEAPON_CLASSES)[number];

/** Stat block an item can contribute. Percentages are fractions (0.08 = 8%). */
export interface ItemStats {
  damage?: number;
  armor?: number;
  hp?: number;
  str?: number;
  agi?: number;
  int?: number;
  vit?: number;
  per?: number;
  capacity?: number;
  accuracy?: number;
  crit?: number;
  escape?: number;
  loot?: number;
  stealth?: number;
  craftSpeed?: number;
  xp?: number;
  expeditionTime?: number;
  healing?: number;
  discovery?: number;
  detect?: number;
  vsInfected?: number;
  vsRaider?: number;
  vsMutant?: number;
  vsDrone?: number;
  vsSurvivor?: number;
  /** Chance to make an attack miss. */
  dodge?: number;
  /** Less damage taken from melee / ranged hits. */
  meleeResist?: number;
  rangedResist?: number;
  /** Better odds for Rare and up when loot drops (multiplier on top of level). */
  luck?: number;
  /** Krug 4 (E5) affixes: +% damage, +% ambush damage, −% encounter chance, +% max durability. */
  damagePct?: number;
  ambush?: number;
  encounter?: number;
  durability?: number;
}
export type ItemStatKey = keyof ItemStats;

export interface ItemAffix {
  stat: ItemStatKey;
  value: number;
  /** Upgrade milestones (+5, +10, +15, +20) that picked this affix: +10% of its value each (krug 5, F9). */
  boost?: number;
  /** 4. 10. 2026 (package 3, 7.g): rolled 1.3× the catalog's best on this item (`items/pristine.ts`); missing: no. */
  pristine?: boolean;
  /**
   * 11. 10. 2026 (BACKLOG #36, Recalibrate): the item's one Recalibrated affix and how many times it was recalibrated (the
   * next price ×1.5 each); missing: never. Kept in the affixes' JSON, so no column and no migration.
   */
  recalibrated?: number;
}

export interface ItemPassive {
  id: string;
  /** Parameters used by the passive and by its description template. */
  value?: number;
}

/** A concrete item instance (gear). Resources and consumables are stacks, not items. */
export interface ItemInstance {
  id: string;
  templateId: string;
  rarity: Rarity;
  ilvl: number;
  durability: number;
  maxDurability: number;
  /** Base stats already scaled by ilvl and rarity. */
  base: ItemStats;
  affixes: ItemAffix[];
  passive: ItemPassive | null;
  /** Upgrade level +N (krug 5, F9); missing on items made before it means +0. */
  upgrade?: number;
  /**
   * 1. 10. 2026 (C4): the item's own tier after a tier upgrade (or a Relic Forge item made on the survivor's
   * tier); missing: its template's tier (`itemTier`).
   */
  tier?: 1 | 2 | 3 | 4;
  /**
   * 4. 10. 2026 (package 3, H): raised by Rework: the rarity it had before the first Rework and what went into the
   * Reworks (dismantling gives half of it back, never the donors). Missing: never reworked.
   */
  rework?: ItemRework | null;
}

/**
 * What went into an item beyond finding it (items.rework): its Reworks (the rarity before the first one and what they
 * cost, half of it back when dismantled) and, for a crafted item, the tier upgrades it really got (5. 10. 2026: a
 * crafted item is made on the survivor's tier, so its tier says nothing about what was paid).
 */
export interface ItemRework {
  from?: Rarity;
  invested?: StackMap;
  tierPaid?: StackMap;
  /**
   * 11. 10. 2026 (owner, BACKLOG #36): the materials and Scrap its Recalibrates cost (after INT's cut; the Relic Shards are
   * not kept): dismantling gives back half of them, as for Rework.
   */
  recal?: StackMap;
}

export type Equipment = Partial<Record<GearSlot, ItemInstance | null>>;

export type SkillRanks = Record<string, number>;

export interface Appearance {
  build: 'lean' | 'sturdy';
  skin: number; // 0..5
  hair: number; // 0..5
  hairColor: number; // 0..5
  beard: number; // 0..3
  outfit: 'moss' | 'rust' | 'steel' | 'sand';
  headwear: 'none' | 'cap' | 'hat' | 'hood';
  pack: 'satchel' | 'hiking' | 'duffel';
  /** The outfit chosen in the wardrobe (docs/OUTFITI_garderoba.md); missing or null: the base look. */
  wardrobe?: { set: SetId; rarity: 'common' | 'rare' | 'epic' | 'legendary' } | null;
  /**
   * 20. 10. 2026 (owner): how the portrait picks its outfit: `gear` ("Match my gear", the default) follows the worn armor,
   * `manual` shows `wardrobe` (an outfit or the base look). Missing: `manual` when an outfit was chosen before, else `gear`
   * (`lookModeOf`).
   */
  look?: 'gear' | 'manual';
}

/** Numbers every fight and every encounter works with. */
export interface CombatStats {
  level: number;
  hpMax: number;
  attack: number;
  defense: number;
  str: number;
  agi: number;
  int: number;
  vit: number;
  per: number;
  /** Attributes from points, gear and perks before the half value above 40 (krug 5, F8.2); the fields above are effective. */
  attrsTotal?: Attributes;
  power: number;
  capacity: number;
  accuracy: number;
  crit: number;
  critMult: number;
  escape: number;
  loot: number;
  stealth: number;
  detect: number;
  dodge: number;
  xp: number;
  expeditionTime: number;
  healing: number;
  discovery: number;
  craftSpeed: number;
  vs: Partial<Record<EnemyType, number>>;
  ranged: boolean;
  loud: boolean;
  /** 7. 10. 2026 (Ammo per shot): the weapon fires Ammo; a run turns up rounds on its searches (`AMMO_RULES.searchRounds`). */
  firearm?: boolean;
  weaponClass: WeaponClass | null;
  /** Skill driven modifiers resolved into plain numbers. */
  adrenaline: number; // extra attack fraction below 30% HP
  executioner: number; // extra damage fraction vs <20% HP
  ambushBonus: number; // extra damage fraction on the ambush round
  fieldMedic: number; // heal fraction of max HP after a won fight
  passives: string[];
  // Added with pants and set bonuses. Optional because ghosts and encounters keep
  // older stat snapshots in the database; read them with `?? 0` / `?? []`.
  meleeResist?: number;
  rangedResist?: number;
  luck?: number;
  /** Heal fraction of max HP after every fight, won or not (Medic 3). */
  postFightHeal?: number;
  /** Extra repair discount (Worker 5). */
  repairDiscount?: number;
  /** Extra scrap when dismantling (Scrapper 3). */
  dismantleBonus?: number;
  setHooks?: SetHookId[];
  /** Initiative change from the equipment load (−0.05 = strikes first less often). */
  initiative?: number;
  /** Equipment load (section 7). */
  load?: import('./items/load').LoadInfo;
  /** Armor from gear (perk target `armored`: 30 or more). */
  armor?: number;
  /** Less encounter chance from affixes (0.05 = −5%). */
  encounter?: number;
  /** Tall Grass (krug 5, F10): never attacked first in this zone (strikes first, no ambush or opening shot against it). */
  alwaysFirst?: boolean;
  // Krug 5 (K10): the skill tree. Optional for old snapshots (ghosts, encounters).
  /** Opening Shot: extra damage fraction on the survivor's first ranged attack of a fight. */
  openingShotDamage?: number;
  /** Deadeye: the survivor's first ranged attack of a fight is always a critical hit. */
  openingShotCrit?: boolean;
  /** Highest dodge (0.3; Phantom 0.45). */
  dodgeCap?: number;
  /** Initiative multiplier from the tree (Slippery: 1.1): AGI × (1 + initiative) × this. */
  initiativeMult?: number;
  /** Hoarder: escape change while the pack is over half full (−0.1). */
  escapeHalfFull?: number;
  /** Ghost: never trades in an encounter. */
  noTrade?: boolean;
  /** Silent Step: encounter chance change (−0.2 = 20% fewer). */
  encounterMod?: number;
  /** Lucky Find: chance of one extra item from a search. */
  extraItem?: number;
  /** Krug 5 (K12.4): how much stronger meals are (Iron Stomach 0.5, Camper 0.15–0.3), at most +100% (1). */
  mealBoost?: number;
  /** Krug 5 (K12.4): Campfire's change of the Food in meal recipes (−0.25 = a quarter less). */
  mealFood?: number;
  /** Machinist and small nodes: recipe materials change (−0.2 = 20% less). */
  craftMaterials?: number;
  /** Machinist: points added to the lowest affix roll of a crafted item (10 = +10%). */
  craftRollPts?: number;
  /**
   * Item perk effects of the worn gear (krug 4). The plain ones are already in the numbers
   * above; the rest are read where they act (combat, loot, encounters). Old snapshots: none.
   */
  perkFx?: import('./items/perks').ActivePerkEffect[];
  /** Package 4, F2 (horde kinds): a defender's flat cut of every hit it takes, and the most one hit does to it. */
  flatReduce?: number;
  hitCap?: number;
  /** Package 4, F2: how much the attacker's ignoring of this defender's defense counts (an armoured horde: 2). */
  ignoreMult?: number;
  /** Package 4, F2: how much the attacker's extra-hit perks count against this defender (a swarm: 2). */
  multiHitMult?: number;
  /** Package 4, F2: an attacker whose missed shot gives the defender a counter-shot for this share of a hit (Tollmen). */
  counterOnDodge?: number;
}

export type ResourceId =
  | 'food'
  | 'wood'
  | 'cloth'
  | 'metal'
  | 'fuel'
  | 'electronics'
  | 'chemicals'
  | 'medicine'
  | 'ammo'
  | 'scrap';

export type ConsumableId =
  | 'bandage'
  | 'medkit'
  | 'painkillers'
  | 'ration'
  | 'dried_jerky'
  | 'hot_stew'
  | 'cold_beans'
  | 'canned_feast'
  | 'stim'
  | 'repair_kit'
  | 'precision_repair_kit'
  | 'mend_shot'
  // Package 4, part O: the new meals.
  | 'station9_chili'
  | 'black_coffee'
  | 'halcyon_bar'
  | 'canned_peaches'
  | 'fever_broth'
  | 'night_stew'
  | 'horde_ration'
  // 7. 10. 2026 (Ammo and Fuel): the ammo box and the bottle.
  | 'hollow_point'
  | 'molotov';

export type SpecialId = 'old_map' | 'map_fragment' | 'blueprint' | 'lab_clue' | 'relic_shard' | 'mend_coupon' | 'red_berries' | 'mercy_postcard' | 'mend_blood' | 'plan_b_binder';

export type StackKind = ResourceId | ConsumableId | SpecialId;

export type StackMap = Partial<Record<StackKind, number>>;

export type ZoneId =
  | 'forest'
  | 'gas_station'
  | 'downtown'
  | 'hospital'
  | 'factory'
  | 'military_base'
  | 'mountains'
  | 'wasteland'
  | 'secret_bunker'
  | 'underground_lab'
  | 'survivor_vault';

export type PvpRule = 'similar' | 'open';

export type BuildingId = 'workshop' | 'storage' | 'medical_tent' | 'barricade';

export type ExpeditionKind = 'normal' | 'horde' | 'raid';

export type ExpeditionStatus = 'active' | 'awaiting' | 'returned' | 'claimed';

export type ExpeditionOutcome = 'returned' | 'retreated' | 'robbed' | 'defeated' | 'full';

export type EncounterAction = 'trade' | 'attack' | 'steal' | 'escape' | 'follow';

export type OpponentKind = 'real' | 'ghost' | 'npc';
